// Dashboard.jsx – v2
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Header from "./Header";

/* ------------------------------------------------------------------------- */
/*  Mock data (replace with API later)                                       */
/* ------------------------------------------------------------------------- */
const mockFiles = [
  {
    id: 1,
    name: "customer_invoices_batch_1.xlsx",
    status: "completed",
    created_at: "2025-06-20T10:30:00Z",
    pdf_count: 25,
    download_url: "#",
  },
  {
    id: 2,
    name: "employee_letters.xlsx",
    status: "processing",
    created_at: "2025-06-20T11:15:00Z",
    pdf_count: 12,
    download_url: null,
  },
  {
    id: 3,
    name: "monthly_statements.xlsx",
    status: "completed",
    created_at: "2025-06-19T14:20:00Z",
    pdf_count: 48,
    download_url: "#",
  },
  {
    id: 4,
    name: "contract_letters.xlsx",
    status: "failed",
    created_at: "2025-06-19T09:45:00Z",
    pdf_count: 0,
    download_url: null,
  },
  {
    id: 5,
    name: "welcome_packages.xlsx",
    status: "uploaded",
    created_at: "2025-06-20T12:00:00Z",
    pdf_count: 0,
    download_url: null,
  },
];

/* ------------------------------------------------------------------------- */
const badge = (st) => {
  const cls =
    {
      uploaded: "bg-blue-50 text-blue-700",
      processing: "bg-yellow-50 text-yellow-700",
      completed: "bg-green-50 text-green-700",
      failed: "bg-red-50 text-red-700",
    }[st] ?? "bg-slate-100 text-slate-700";
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>
      {st}
    </span>
  );
};
const prettyDate = (d) =>
  new Date(d).toLocaleString("en-US", {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

export default function Dashboard() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  /* data + UI state */
  const [files, setFiles] = useState([]);
  const [query, setQuery] = useState("");
  const [toast, setToast] = useState(null);

  useEffect(() => {
    // mimic API
    const t = setTimeout(() => setFiles(mockFiles), 700);
    return () => clearTimeout(t);
  }, []);

  /* ---------------------------------- actions */
  const pushToast = (msg, type = "info") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  const download = (f) => {
    if (f.status === "completed" && f.download_url)
      pushToast(`Downloading ${f.name}`, "success");
    else pushToast(`Download not available`, "error");
  };
  const retry = (f) => {
    pushToast(`Retrying job…`, "info");
    setFiles((v) =>
      v.map((x) => (x.id === f.id ? { ...x, status: "processing" } : x))
    );
    setTimeout(() => {
      setFiles((v) =>
        v.map((x) =>
          x.id === f.id ? { ...x, status: "completed", download_url: "#" } : x
        )
      );
      pushToast(`Job finished!`, "success");
    }, 1500);
  };

  /* ---------------------------------- filter */
  const filtered = files.filter((f) =>
    f.name.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-stone-50 to-amber-50">
      <Header />

      {toast && (
        <div
          className={`fixed top-6 left-1/2 -translate-x-1/2 z-50 px-6 py-3 rounded-md shadow-lg text-white
          ${
            toast.type === "success"
              ? "bg-green-600"
              : toast.type === "error"
              ? "bg-red-600"
              : "bg-amber-600"
          }`}
        >
          {toast.msg}
        </div>
      )}

      {/* Hero */}
      <section className="text-center py-10">
        <h1 className="text-4xl font-extrabold text-slate-800">
          Hi, {user?.email || "User"}!
        </h1>
        <p className="mt-2 text-slate-600">
          Here’s what’s happening with your files 👇
        </p>
      </section>

      {/* Quick stats */}
      <section className="bg-white/70 backdrop-blur border-y py-6">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 text-center gap-6 text-slate-700">
          <Stat num={files.length} label="Files" />
          <Stat
            num={files.reduce((n, f) => n + f.pdf_count, 0)}
            label="Total PDFs"
            color="text-amber-600"
          />
          <Stat num="99.9%" label="Uptime" />
          <Stat num="SOC 2" label="Compliance" />
        </div>
      </section>

      {/* Controls */}
      <div className="max-w-7xl mx-auto px-4 mt-10 flex flex-wrap justify-between items-center gap-4">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search file name…"
          className="flex-1 min-w-[200px] border px-4 py-2 rounded shadow-sm focus:ring-amber-500"
        />
        <div className="flex gap-3">
          <Btn to="/upload" label="Upload" style="white" />
          <Btn to="/templates" label="Templates" style="white" />
          <Btn to="/jobs" label="PDF Jobs" style="white" />
        </div>
      </div>

      {/* File cards */}
      <div className="max-w-7xl mx-auto px-4 mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((f) => (
          <div
            key={f.id}
            className="relative group bg-white rounded-xl shadow hover:shadow-2xl p-6 transition"
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <h3 className="font-semibold text-slate-800 truncate w-56">
                {f.name}
              </h3>
              {badge(f.status)}
            </div>
            <div className="text-xs text-slate-500 mb-4">
              Created {prettyDate(f.created_at)}
            </div>
            <div className="text-xs text-slate-500 mb-6">
              PDFs generated: {f.pdf_count}
            </div>

            {/* slide-in actions */}
            <div
              className="inset-y-0  translate-x-full group-hover:translate-x-0 w-28 bg-white/90 backdrop-blur
                            border-l flex flex-col justify-center items-center gap-3 transition"
            >
              {f.status === "completed" && (
                <button
                  onClick={() => download(f)}
                  className="text-xs bg-amber-600 hover:bg-amber-700 text-white px-3 py-1 rounded-md"
                >
                  Download
                </button>
              )}
              {f.status === "failed" && (
                <button
                  onClick={() => retry(f)}
                  className="text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-md"
                >
                  Retry
                </button>
              )}
              {f.status === "processing" && (
                <span className="flex flex-col items-center">
                  <span className="animate-spin w-4 h-4 border-2 border-amber-600 border-t-transparent rounded-full mb-1"></span>
                  <span className="text-[10px] text-amber-600">Processing</span>
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <footer className="mt-20 py-8 bg-slate-900 text-stone-200">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center">
          <span className="font-bold tracking-wide mb-3 md:mb-0">
            Letterly © 2025
          </span>
          <nav className="flex gap-6">
            {["Twitter", "LinkedIn", "GitHub"].map((s) => (
              <a key={s} href="#" className="hover:text-amber-400">
                {s}
              </a>
            ))}
          </nav>
        </div>
      </footer>
    </div>
  );
}

/* ------------------------------ sub-components ------------------------------ */
const Stat = ({ num, label, color = "text-slate-800" }) => (
  <div>
    <div className={`text-3xl font-bold ${color}`}>{num}</div>
    <div className="text-sm">{label}</div>
  </div>
);
const Btn = ({ to, label, style }) => (
  <Link
    to={to}
    className={`bg-white border border-slate-300 text-slate-700 px-6 py-2 rounded-full shadow hover:bg-slate-100 transition`}
  >
    {label}
  </Link>
);
